import { PaymentMethodEnum } from '@systemeio/ui'
import {
  UNLIMITED_CRM_PIPELINE,
  UNLIMITED_TRESHOLD,
  UNLIMITED_VARIANTS_TRESHOLD,
} from 'modules/profile/user-plan/constants/user-plan-consts'
import {
  BillingPeriodEnum,
  SubscriptionPeriodEnum,
} from 'modules/profile/user-plan/enums/billing-period-enum'
import {
  AvailablePlansEnum,
  UserPlanTypeEnum,
} from 'modules/profile/user-plan/enums/user-plan-type-enum'
import {
  UseUserPlanInterface,
  UserPlanColumnsInterface,
  UserPlanInterface,
} from 'modules/profile/user-plan/types/user-plan-interface'
import { TLocoType } from 'shared/hooks/use-loco-translation'

export const getPricePlanFeatureTitle = ({
  pricePlan,
  t,
  key,
}: {
  pricePlan: UserPlanInterface
  t: TLocoType
  key: UserPlanColumnsInterface['key']
}) => {
  const pricePlanFeatureValue = pricePlan[key]

  switch (key) {
    case 'maxProductVariants': {
      if (pricePlanFeatureValue >= UNLIMITED_VARIANTS_TRESHOLD) {
        return t('global.unlimited')
      }
      break
    }
    case 'maxCrmPipelines': {
      if (pricePlanFeatureValue >= UNLIMITED_CRM_PIPELINE) {
        return t('global.unlimited')
      }
      break
    }
    default: {
      if (pricePlanFeatureValue >= UNLIMITED_TRESHOLD) {
        return t('global.unlimited')
      }
    }
  }

  return pricePlanFeatureValue
}

export const isPricePlanFeatureExceeded = (
  userResources: UseUserPlanInterface['userResources'],
  pricePlan: UserPlanInterface,
  column: UserPlanColumnsInterface,
) => {
  const pricePlanFeatureValue = pricePlan[column.key]

  if (!column.resourceKey) {
    return false
  }

  return userResources[column.resourceKey] > pricePlanFeatureValue
}

const paymentMethodsWithoutOneClick = [
  PaymentMethodEnum.dlocal_card,
  PaymentMethodEnum.dlocal_boleto,
  PaymentMethodEnum.dlocal_pix,
  PaymentMethodEnum.paypal_soap,
  PaymentMethodEnum.paypal_rest,
]

export function isOneClickAvailable(
  paymentMethod: UseUserPlanInterface['customer']['paymentMethod'],
) {
  if (!paymentMethod) return false
  return !paymentMethodsWithoutOneClick.includes(paymentMethod)
}

// for reference see:
// https://docs.google.com/spreadsheets/d/1CKZ_KPvZW-jUIvRgUFBID6cO7PIyevBnbsC9LowzHHA
const getPlanUpgradesList = (planId: number) => {
  switch (planId) {
    case AvailablePlansEnum.freemium:
      return [
        AvailablePlansEnum.startupNew,
        AvailablePlansEnum.webinar,
        AvailablePlansEnum.enterprise,
      ]
    case AvailablePlansEnum.startup:
      return [
        AvailablePlansEnum.startupNew,
        AvailablePlansEnum.webinar,
        AvailablePlansEnum.enterprise,
      ]
    case AvailablePlansEnum.startupNew:
      return [AvailablePlansEnum.webinar, AvailablePlansEnum.enterprise]
    case AvailablePlansEnum.webinar:
      return [AvailablePlansEnum.enterprise]
    default:
      return []
  }
}

const isLegacyPlanThatShouldNotBeUpgradedTo = (planId: number) => {
  return [AvailablePlansEnum.startup].includes(planId)
}

// we can upgrade in one way Freemium => Startup => Webinar => Enterprise
export const isPlanUpgradeAvailable = (
  pricePlan: UserPlanInterface,
  billingPeriod: BillingPeriodEnum,
  currentUserPlanData: UseUserPlanInterface,
) => {
  const { userPlan: currentUserPlan, subscriptionPeriod: currentBillingPeriod } =
    currentUserPlanData

  if (!currentUserPlan || !currentBillingPeriod) {
    return false
  }

  // custom price plans cannot upgrade to regular ones
  if (
    currentUserPlan.type === UserPlanTypeEnum.promotion &&
    currentUserPlan.id !== AvailablePlansEnum.startup
  ) {
    return false
  }

  // annual subscription can only be upgraded to another annual subscriptions
  if (
    currentBillingPeriod === SubscriptionPeriodEnum.year &&
    billingPeriod === BillingPeriodEnum.monthly
  ) {
    return false
  }

  if (isLegacyPlanThatShouldNotBeUpgradedTo(pricePlan.id)) {
    return false
  }

  // monthly -> annually same price plan one-way upgrade
  if (
    currentBillingPeriod === SubscriptionPeriodEnum.month &&
    currentUserPlan.id === pricePlan.id &&
    billingPeriod === BillingPeriodEnum.annual
  ) {
    return true
  }

  if (getPlanUpgradesList(currentUserPlan.id).includes(pricePlan.id)) {
    return true
  }

  return false
}

export default function getBillingPeriodFromSubscriptionPeriod(
  subscriptionPeriod: SubscriptionPeriodEnum | null,
) {
  if (!subscriptionPeriod) {
    return
  }

  return subscriptionPeriod === SubscriptionPeriodEnum.month
    ? BillingPeriodEnum.monthly
    : BillingPeriodEnum.annual
}

export const isUserPlanActive = (
  pricePlan: UserPlanInterface,
  data: UseUserPlanInterface,
  billingPeriod: BillingPeriodEnum,
) => {
  const isPaidPlanMatched =
    data.userPlan &&
    pricePlan.id === data.userPlan.id &&
    getBillingPeriodFromSubscriptionPeriod(data.subscriptionPeriod) === billingPeriod

  const isFreemiumMatched =
    data.userPlan &&
    pricePlan.id === data.userPlan.id &&
    data.userPlan?.type === UserPlanTypeEnum.freemium

  return isPaidPlanMatched || isFreemiumMatched
}

export const isFeatureExceeded = (
  data: UseUserPlanInterface,
  billingPeriod: BillingPeriodEnum,
  column: UserPlanColumnsInterface,
) => {
  const calculatedUserPlans =
    data.userPlan && data.userPlan.type === UserPlanTypeEnum.promotion
      ? [data.userPlan, ...data.userPlans]
      : data.userPlans

  const activePricePlan = calculatedUserPlans.find(pricePlan =>
    isUserPlanActive(pricePlan, data, billingPeriod),
  )

  if (activePricePlan) {
    return isPricePlanFeatureExceeded(data.userResources, activePricePlan, column)
  }

  return false
}
